import { useQuery } from "@tanstack/react-query";

import { appointmentsApi } from "@/api";
import { ALL_COUNSELORS_OPTION_ID } from "@/common/components/counselor-select/FormikCounselorSelect";
import { QueryKey } from "@/query/Query";

/**
 * The next free slots (unassigned, future, überbuchbar purpose) for one purpose — of one counselor,
 * or of all counselors when "Alle Berater:innen" is chosen. Disabled until both filters are chosen;
 * changing the counselor or purpose reloads the list.
 * `isError` is part of the contract: without it a failed request is indistinguishable from a
 * combination that genuinely has no slots, and the list would claim the latter.
 */
export function useFreeAppointmentSlotsQuery(purposeKey: string, counselorId: string | null) {
  const enabled = purposeKey !== "" && counselorId !== null;

  const {
    data: slots = [],
    isLoading,
    isError,
    // Nested under the Appointments key on purpose: booking a slot invalidates that prefix, so the
    // freed-up list refreshes without the mutation having to know about this query.
  } = useQuery({
    queryKey: [QueryKey.Appointments, "free-slots", purposeKey, counselorId],
    queryFn: () =>
      appointmentsApi.getFreeAppointmentSlots({
        purposeKey,
        // "Alle Berater:innen" is not a counselor id; leaving the filter out is what asks the backend
        // for every counselor's slots.
        assignedCounselorId: counselorId === ALL_COUNSELORS_OPTION_ID ? undefined : (counselorId ?? undefined),
      }),
    enabled,
  });

  return { slots, isLoading, isError };
}
